import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import { api } from '../lib/api'
import { useLocaleStore } from './locale'
import type { UserProfile, UserProfileCreate, UserProfileUpdate } from '../types'

export const useUserProfileStore = defineStore('userProfile', () => {
  const profile = ref<UserProfile | null>(null)
  const loading = ref(false)
  const loaded = ref(false)
  const fetchError = ref(false)

  const usesExternalPartners = computed(() => profile.value?.uses_external_partners ?? true)
  const isTourOperator = computed(() => profile.value?.is_tour_operator ?? true)
  const onboardingComplete = computed(() => !!profile.value?.onboarding_completed_at)
  const companyName = computed(() => profile.value?.company_name ?? '')

  async function fetchProfile() {
    loading.value = true
    fetchError.value = false
    try {
      profile.value = await api.getProfile()

      // Auto-migrate existing users who have no profile but have plans
      if (!profile.value) {
        try {
          const plans = await api.getPlans()
          if (plans.length > 0) {
            const email = (await import('../lib/supabase')).supabase.auth
              .getSession()
              .then(r => r.data.session?.user?.email ?? '')
            const userEmail = await email
            const companyName = deriveCompanyName(userEmail)
            profile.value = await api.createProfile({
              company_name: companyName,
              is_tour_operator: true,
              uses_external_partners: true,
            })
          }
        } catch {
          // Auto-migration failed — user will see onboarding wizard instead
        }
      }

      if (profile.value?.preferred_language) {
        useLocaleStore().syncFromProfile(profile.value.preferred_language)
      }

      loaded.value = true
    } catch {
      fetchError.value = true
      loaded.value = true
    } finally {
      loading.value = false
    }
  }

  function deriveCompanyName(email: string): string {
    if (!email || !email.includes('@')) return 'My Company'
    const domain = email.split('@')[1]?.split('.')[0]
    if (!domain) return 'My Company'
    return domain.charAt(0).toUpperCase() + domain.slice(1)
  }

  async function createProfile(data: UserProfileCreate): Promise<UserProfile> {
    const created = await api.createProfile(data)
    profile.value = created
    return created
  }

  async function updateProfile(data: UserProfileUpdate): Promise<UserProfile> {
    const updated = await api.updateProfile(data)
    profile.value = updated
    return updated
  }

  function $reset() {
    profile.value = null
    loading.value = false
    loaded.value = false
    fetchError.value = false
  }

  return {
    profile,
    loading,
    loaded,
    fetchError,
    usesExternalPartners,
    isTourOperator,
    onboardingComplete,
    companyName,
    fetchProfile,
    createProfile,
    updateProfile,
    $reset,
  }
})
